import React, { useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import moment from 'moment-mini';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MessageDateSeparator from '@/components/Messages/MessageDateSeparator';
import { resolveSourcesOutputsLayout } from '@/components/Chat/sourcesOutputsLayout';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getEffectiveContextSidebarWidth, getNavigationSidebarWidth, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import WorkStartupCompanion from './WorkStartupCompanion';
import WorkLaunchEntrance from './WorkLaunchEntrance';

export default function WorkLaunchPreview({ launch }) {
  const { colors } = useTheme();
  const { isXS, windowWidth } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const showTimestamps = useUIStore(state => state.showTimestamps);
  const showSources = useUIStore(state => state.showSourcesOutputsPanel !== false);
  const sidebarsWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth)
    + getEffectiveContextSidebarWidth(state, windowWidth));
  const [mountedAt] = useState(Date.now);
  const startedAt = launch.startedAt || mountedAt;
  const column = { maxWidth: MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'] || MESSAGE_WIDTH_AMOUNTS.NARROW };
  const layout = resolveSourcesOutputsLayout({ chatPaneWidth: (windowWidth || 0) - sidebarsWidth });
  const reservedRight = Platform.OS === 'web' && !isXS && showSources ? layout.reservedRight : 0;

  return <WorkLaunchEntrance startedAt={startedAt} style={styles.surface}>
    <ScrollView style={[styles.scroll, { marginRight: reservedRight }]}
      contentContainerStyle={[styles.content, { paddingTop: isXS ? 15 : 20 }]}>
      <MessageDateSeparator timestamp={startedAt} messageWidth={messageWidth} />
      <View style={[styles.column, column]}>
        <View style={styles.messageRow}>
          <View style={styles.messageWrapper}>
            <View style={[styles.message, { backgroundColor: colors.solid_color_user_bubble }]}>
              {!!launch.prompt && <Text size="m" style={styles.prompt}>{launch.prompt}</Text>}
              {launch.fileCount > 0 && <Text size="s" color="secondary" style={styles.attachments}>
                {launch.fileCount} {launch.fileCount === 1 ? 'attachment' : 'attachments'}
              </Text>}
            </View>
            {!!showTimestamps && <Text size="xs" style={styles.timestamp}>{moment(startedAt).format('LT')}</Text>}
          </View>
        </View>
        <WorkStartupCompanion phase={launch.phase} startedAt={startedAt} prompt={launch.prompt} />
        {!!launch.projectName && <View style={styles.project}>
          <Icon name="laptop" size={14} color={colors.text_color_secondary} />
          <View style={styles.projectText}>
            <Text size="s" color="secondary">{launch.projectName}</Text>
            <Text size="s" color="secondary">Keep Gab AI open on your computer.</Text>
          </View>
        </View>}
      </View>
    </ScrollView>
  </WorkLaunchEntrance>;
}

const styles = StyleSheet.create({
  surface: { flex: 1, width: '100%', minWidth: 0, alignSelf: 'stretch' },
  scroll: { flex: 1, minWidth: 0 },
  content: { flexGrow: 1, paddingBottom: 32 },
  column: { width: '100%', minWidth: 0, alignSelf: 'center', paddingHorizontal: 16 },
  // Match MessageBubble's column, bubble, timestamp and reserved action row so
  // the prompt and companion stay in place when ChatView takes over.
  messageRow: { alignItems: 'flex-end', paddingTop: 8, paddingBottom: 38, marginBottom: 8 },
  messageWrapper: { maxWidth: '92.5%', minWidth: 0 },
  message: { borderRadius: 20, paddingHorizontal: 12, paddingVertical: 8 },
  prompt: { flexShrink: 1, lineHeight: 22.5, ...(Platform.OS === 'web' && { overflowWrap: 'anywhere' }) },
  attachments: { marginTop: 8 },
  timestamp: { opacity: 0.5, marginTop: 3, textAlign: 'right', paddingHorizontal: 4 },
  project: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginTop: 12, paddingLeft: 44 },
  projectText: { flex: 1, minWidth: 0, gap: 4 },
});
